/**
 * SOLID Review
 * S — Good. Two concrete transports (GraphQL over Apollo, REST over fetch) plus their Null objects.
 *     One concern — crossing the wire — expressed twice because there are two protocols.
 * O — Good. A new domain reuses these; a new protocol is a third pair.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Each class implements exactly one narrow port.
 * D — Good. Depends on the port interfaces and the Apollo client instance it is CONSTRUCTED with,
 *     not on a module-level singleton.
 */
/**
 * DDD Layer: Infrastructure — the concrete client half of the client↔server seam.
 *
 * ONE INSTANCE PER BOUNDED CONTEXT, and the honest accounting of what that buys: all six GraphQL
 * clients wrap the same Apollo client, so this adds no substitutability — you cannot swap the room
 * domain's transport without swapping the round domain's. It adds ENUMERABILITY, and it makes the
 * seam drawable and countable. `FrameworkConnectorAssembly.ts` made exactly this admission about
 * its five lollipops supplying one ComponentApplication ("This adds no substitutability. It adds
 * enumerability"), and the same sentence is true here.
 *
 * What it DOES buy beyond drawing: a domain's adapter can only execute through the socket its
 * domain owns, so deleting a socket breaks that domain and nothing else — which is what makes the
 * eight seams independently meaningful even though the transport underneath is shared.
 */
import type { IPortRestApiClient } from './ports';

/**
 * NULL GraphQL client — the frontend's degradation rule (rev4 E.1).
 *
 * Every frontend socket is RequiredPortWithDefault, never RequiredPort, because a UI that throws is
 * a white screen. Reads resolve to nothing and subscriptions never fire, which renders an empty but
 * live interface. `assertAssembled` still fails the BOOT on any unconnected port not on the
 * allowlist, so this is degradation of last resort rather than a way for a missing wire to pass.
 *
 * The casts are deliberate and confined to this file. `IPortGraphQLDomainClient` is
 * `Pick<ApolloClient, …>`, so implementing it honestly would mean reproducing Apollo's full generic
 * result types — for an object whose entire purpose is to do nothing. A Null object is the one
 * place where "satisfies the shape, ignores the semantics" is the intent rather than a shortcut.
 *
 * There is no non-null GraphQLDomainClient class: the real socket is filled with the app's Apollo
 * client itself. A pass-through wrapper forwarding one call per method is ceremony, not a seam.
 */
export class NullGraphQLDomainClient {
  query = async (): Promise<unknown> => ({ data: null });
  mutate = async (): Promise<unknown> => ({ data: null });
  subscribe = (): unknown => ({ subscribe: () => ({ unsubscribe: () => undefined }) });
}

/**
 * A refusal the server ANSWERED: its own words, and the HTTP status they came with (frontend strict-ports
 * plan, FE3). An `Error`, so every caller that shows `err.message` still does; the status is what lets the
 * auth gateway tell "there is no player behind this token" (401, 404) from "the server could not be asked"
 * — a fetch that failed throws the browser's own error, never this.
 */
export class RestApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'RestApiError';
    this.status = status;
  }
}

/** REST transport for one client-facing surface. */
export class RestApiClient implements IPortRestApiClient {
  constructor(private readonly baseUrl: string) {}

  async request<T>(
    path: string,
    init: { method: string; body?: unknown; jwt?: string | null },
    parse: (data: unknown) => T,
  ): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (init.jwt) headers['Authorization'] = `Bearer ${init.jwt}`;

    const res = await fetch(`${this.baseUrl}${path}`, {
      method: init.method,
      headers,
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    });

    // 204 has no body by contract (DELETE /api/legal/me), so do not ask fetch to parse one.
    //
    // `.catch(() => undefined)` added 2026-08-30, when LegalApiAdapter moved onto this client and
    // its suite caught the difference: a 503 with a non-JSON body (an nginx error page, say) threw
    // the JSON parse error and LOST the status, so the user saw "not json" instead of
    // "Request failed: 503". The status is the useful part of that message.
    const data: unknown = res.status === 204 ? undefined : await res.json().catch(() => undefined);

    if (!res.ok) {
      const err = data as { error?: string } | undefined;
      throw new RestApiError(err?.error ?? `Request failed: ${res.status}`, res.status);
    }
    // PARSED against the contract, not cast. The caller passes the schema's parse function, so a
    // server that stops sending a declared field is an error here rather than an undefined carried
    // into the store.
    return parse(data);
  }
}

/** NULL REST client — same degradation rule as above. */
export class NullRestApiClient implements IPortRestApiClient {
  async request<T>(_path: string, _init: unknown, _parse: (data: unknown) => T): Promise<T> {
    throw new Error('RestApiClient is not connected');
  }
}
